import React, { useEffect, useRef, useState } from "react";
import OffersList from "./component/OffersList";
import PageHeader from "../../Component/BasicComponents/HeaderBreadCrumbs";
import RouteModal from "../../Component/BasicComponents/Modal/RouteModal";
import { handleMedia } from "../edetailing/repository/EdetailingRepository";
import { useGetOrgUnitsData } from "../Configuration/Grade Master/hooks/useGradeMaster";
import RouteDropdown from "../../Component/BasicComponents/Dropdowns/RouteDropdown";
import { useAddOffers, useUpdateOffers } from "./hooks/useOffers";
import CustomInput from "../../Component/BasicComponents/CustomInput";
import { showToast } from "../../utils/toastUtils";
import { FaTrash, FaTrashAlt, FaUpload } from "react-icons/fa";
import { Label, Spinner } from "reactstrap";

const AddOfferModalContent = ({
  selectedRowData,
  isSubmitting,
  setIsSubmitting,
  modalStatus,
  setIsModalOpen,
}: {
  selectedRowData: any;
  isSubmitting: boolean;
  setIsSubmitting: (val: boolean) => void;
  modalStatus?: string;
  setIsModalOpen: (val: boolean) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [orgUnit, setOrgUnit] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [media, setMedia] = useState<File | null | string | number>("");
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isImageLoadingSpinner, setIsImageLoadingSpinner] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const BASE_URL = "https://tspc-development.truesales.in/media?id=";

  const { data: orgUnitsData } = useGetOrgUnitsData({
    enable: true,
  });

  const { mutate: addOffers } = useAddOffers();
  const { mutate: updateOffers } = useUpdateOffers();

  const formattedOrgUnitData = Object.entries(orgUnitsData?.data || {}).map(
    (item: any) => ({
      id: item[0],
      label: item[1],
    })
  );

  useEffect(() => {
    if (selectedRowData && modalStatus === "Edit") {
      setTitle(selectedRowData?.Title || "");
      setDescription(selectedRowData?.Description || "");
      setOrgUnit(selectedRowData?.OrgUnitId || "");
      setStartDate(selectedRowData?.StartDate || "");
      setEndDate(selectedRowData?.EndDate || "");
      setMedia(selectedRowData?.MediaId || "");
    }
  }, [selectedRowData]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // ✅ Safe check
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setOrgUnit("");
    setStartDate("");
    setEndDate("");
    setMedia("");
    setModalImageUrl("");
    setIsImageLoadingSpinner(false);
  };

  const internalHandleClose = () => {
    resetForm();
    setIsSubmitting(false);
    handleClose();
  };

  const handleImageClick = (imageUrl: string) => {
    setModalImageUrl(imageUrl);
    setIsImageModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (modalStatus === "Edit") {
      if (!title) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the Title Field!");
        return;
      } else if (!description) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the Description Field!");
        return;
      } else if (!orgUnit) {
        setIsSubmitting(false);
        showToast.warning("Please Select the Org Unit Field!");
        return;
      } else if (!startDate) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the Start Date Field!");
        return;
      } else if (!endDate) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the End Date Field!");
        return;
      } else if (!media) {
        setIsSubmitting(false);
        showToast.warning("Please Select a Media!");
        return;
      } else if (new Date(startDate) > new Date(endDate)) {
        setIsSubmitting(false);
        showToast.warning("Start Date Should not be greater than End Date!");
        return;
      } else {
        const updateOfferBody = {
          Title: title,
          Description: description,
          OrgUnitId: orgUnit,
          StartDate: startDate,
          EndDate: endDate,
          MediaId: media,
        };
        updateOffers(
          { id: selectedRowData?.Id, body: updateOfferBody },
          {
            onSuccess: () => {
              internalHandleClose();
            },
            onSettled: () => {
              setIsSubmitting(false);
            },
          }
        );
      }
    } else {
      if (!title) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the Title Field!");
        return;
      } else if (!description) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the Description Field!");
        return;
      } else if (!orgUnit) {
        setIsSubmitting(false);
        showToast.warning("Please Select the Org Unit Field!");
        return;
      } else if (!startDate) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the Start Date Field!");
        return;
      } else if (!endDate) {
        setIsSubmitting(false);
        showToast.warning("Please Enter the End Date Field!");
        return;
      } else if (!media) {
        setIsSubmitting(false);
        showToast.warning("Please Select a Media!");
        return;
      } else if (new Date(startDate) > new Date(endDate)) {
        setIsSubmitting(false);
        showToast.warning("Start Date Should not be greater than End Date!");
        return;
      } else {
        const addOffersBody = {
          Title: title,
          Description: description,
          OrgUnitId: orgUnit,
          StartDate: startDate,
          EndDate: endDate,
          MediaId: media,
        };

        addOffers(addOffersBody, {
          onSuccess: () => {
            internalHandleClose();
          },
          onSettled: () => {
            setIsSubmitting(false);
            setIsImageLoadingSpinner(false);
          },
        });
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setIsImageLoadingSpinner(true);
        const mediaId = await handleMedia(file);
        setMedia(mediaId);
      } catch (error) {
        console.error("Media upload failed:", error);
        setIsImageLoadingSpinner(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <label style={{ fontWeight: "500", marginTop: "5px", fontSize: "20px" }}>
        {modalStatus === "Edit" ? "Edit Offer" : "Add Offer"}
      </label>

      {/* Title Input */}
      <div className="mb-3">
        <CustomInput
          label="Offer Title"
          asterisk={true}
          placeholder="Enter Offer Title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {/* Description Input */}
      <div className="mb-3">
        <CustomInput
          label="Offer Description"
          asterisk={true}
          placeholder="Enter Offer Description"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {/* Org Unit Dropdown */}
      <div className="col-md">
        <RouteDropdown
          options={formattedOrgUnitData || []}
          value={orgUnit}
          onChange={(value: any) => {
            setOrgUnit(value);
          }}
          DropdownHeading="Org Unit"
          placeholder="Select Org Unit"
          asterisk={true}
        />
      </div>

      {/* Start & End Date Inputs */}
      <div className="row mb-3 mt-3">
        <div className="col-md-6">
          <CustomInput
            label="Start Date"
            asterisk={true}
            placeholder="Enter Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <CustomInput
            label="End Date"
            asterisk={true}
            placeholder="Enter End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* Media Upload */}
      <div className="mb-3">
        <Label>
          {" "}
          Upload Media <span style={{ color: "red", fontSize: "14px" }}>*</span>
        </Label>
        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        {/* Custom Upload Button */}
        <button
          type="button"
          onClick={handleButtonClick}
          className="btn btn-primary d-flex align-items-center gap-2"
          style={{
            borderRadius: "8px",
            padding: "8px 14px",
            backgroundColor: "#43bab2",
            border: "none",
          }}
        >
          <FaUpload size={14} /> Upload Media
        </button>
      </div>

      {media ? (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            marginTop: "8px",
          }}
        >
          <img
            src={
              media
                ? `${BASE_URL}${media}`
                : "https://via.placeholder.com/100?text=No+Image"
            }
            alt="Uploaded Media"
            style={{
              width: "100px",
              height: "100px",
              borderRadius: "8px",
              marginTop: "8px",
              cursor: "pointer",
            }}
            onClick={() => handleImageClick(`${BASE_URL}${media}`)}
          />
          <FaTrash
            size={18}
            color="white"
            style={{
              position: "absolute",
              top: "4px",
              right: "1px",
              backgroundColor: "rgba(255, 0, 0, 0.8)",
              borderRadius: "50%",
              padding: "4px",
              cursor: "pointer",
              transition: "transform 0.2s ease",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.transform = "scale(1.1)")
            }
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onClick={() => {
              setMedia(null);
              setIsImageLoadingSpinner(false);
            }}
            title="Remove Image"
          />
        </div>
      ) : (
        isImageLoadingSpinner && (
          <Spinner color="primary" style={{ width: "1rem", height: "1rem" }} />
        )
      )}

      {/* Buttons */}
      <div className="d-flex justify-content-start mt-3 gap-2">
        <button
          className="btn"
          style={{ backgroundColor: "#43B9B2", color: "white" }}
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Submitting..."
            : modalStatus === "Edit"
            ? "Update"
            : "Submit"}
        </button>
        <button
          className="btn"
          style={{
            backgroundColor: "#F9FAFC",
            color: "#000",
            border: "1px solid #E0E0E0",
          }}
          type="button"
          onClick={internalHandleClose}
        >
          Cancel
        </button>
      </div>

      <RouteModal
        isOpen={isImageModalOpen}
        toggle={() => setIsImageModalOpen(false)}
        title="Image Preview"
        size="lg"
        content={
          <div style={{ textAlign: "center" }}>
            <img
              src={modalImageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                borderRadius: "8px",
              }}
            />
          </div>
        }
      />
    </form>
  );
};

const Offers: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [modalStatus, setModalStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddOffer = () => {
    setModalStatus("Add");
    setIsModalOpen(true);
  };

  const handleToggleModal = () => {
    setIsModalOpen(false);
  };

  //================================ Modal Code Here====================================

  return (
    <div className="p-2">
      <PageHeader
        title="Offers"
        buttons={[
          {
            label: "+Add Offers",
            onClick: handleAddOffer,
            color: "#2e8dd3",
          },
        ]}
      />
      <div
        style={{
          display: "flex",
          gap: "20px",
          width: "100%",
        }}
      >
        <div style={{ flex: "1", minWidth: "100%", maxWidth: "100%" }}>
          <OffersList
            setSelectedRowData={setSelectedRowData}
            setIsModalOpen={setIsModalOpen}
            setModalStatus={setModalStatus}
          />
        </div>
      </div>
      <RouteModal
        isOpen={isModalOpen}
        toggle={handleToggleModal}
        size="md"
        content={
          <AddOfferModalContent
            selectedRowData={selectedRowData}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            modalStatus={modalStatus}
            setIsModalOpen={setIsModalOpen}
          />
        }
      />
    </div>
  );
};

export default Offers;
